<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import TextArea from 'shared/components/TextArea.vue';

const store = useStore();
const { t } = useI18n();

const votedValue = ref(null);
const feedbackComment = ref('');
const submittedFeedback = ref({});
const displayMode = ref('none');

const valuesRequiringComment = ['poor', 'wrong'];
const currentUser = useMapGetter('getCurrentUser');
const { id, content, conversationId, additionalAttributes } =
  useMessageContext();

const trackingData = () => {
  return {
    at: new Date().toISOString(),
    agent: currentUser.value.email,
  };
};

const submitFeedback = async updateData => {
  const newAttrs = await store.dispatch('updateAdditionalAttributes', {
    conversationId: conversationId.value,
    messageId: id.value,
    data: { chatbot: updateData },
  });
  submittedFeedback.value = newAttrs?.chatbot || {};
  useAlert(t('CONVERSATION.CHATBOT.FEEDBACK.THANK_YOU_MESSAGE'));
};

const onClickFeedback = async value => {
  votedValue.value = value;
  submitFeedback({
    feedback: votedValue.value,
    submitted: trackingData(),
  });
  displayMode.value = valuesRequiringComment.includes(value)
    ? 'add_comment'
    : 'submitted';
};

const onSubmitComment = async () => {
  submitFeedback({
    feedback: votedValue.value,
    submitted: trackingData(),
    comment: votedValue.value === 'good' ? null : feedbackComment.value,
  });
  displayMode.value = 'submitted';
};

const formatDate = dateString => {
  const date = new Date(dateString);
  return date.toLocaleString('default', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

onMounted(() => {
  submittedFeedback.value = additionalAttributes.value?.chatbot || {};
  if (additionalAttributes.value?.chatbot?.feedback) {
    displayMode.value = 'submitted';
  }
  if (additionalAttributes.value?.chatbot?.comment) {
    feedbackComment.value = additionalAttributes.value?.chatbot?.comment;
  }
});
</script>

<template>
  <BaseBubble class="px-4 py-3 min-w-[32rem]" data-bubble-name="text">
    <div class="mb-6">
      {{ content }}
    </div>
    <div>
      <p class="text-xs mb-2 text-center text-n-slate-10">
        {{ $t('CONVERSATION.CHATBOT.FEEDBACK.TITLE') }}
      </p>
      <ul class="list-none w-full flex justify-center gap-2 px-3">
        <li class="inline">
          <NextButton
            outline
            faded
            xs
            teal
            icon="i-ph-thumbs-up"
            :disabled="submittedFeedback.feedback === 'good'"
            :label="$t('CONVERSATION.CHATBOT.FEEDBACK.GOOD')"
            @click="onClickFeedback('good')"
          />
        </li>
        <li class="inline">
          <NextButton
            outline
            faded
            xs
            amber
            icon="i-ph-thumbs-down"
            :disabled="submittedFeedback.feedback === 'poor'"
            :label="$t('CONVERSATION.CHATBOT.FEEDBACK.POOR')"
            @click="onClickFeedback('poor')"
          />
        </li>
        <li class="inline">
          <NextButton
            outline
            faded
            xs
            ruby
            icon="i-ph-x"
            :disabled="submittedFeedback.feedback === 'wrong'"
            :label="$t('CONVERSATION.CHATBOT.FEEDBACK.WRONG')"
            @click="onClickFeedback('wrong')"
          />
        </li>
      </ul>
      <div v-if="displayMode === 'submitted'" class="mt-4">
        {{ con }}
        <p class="text-xs italic text-n-slate-10 mb-0">
          {{
            $t('CONVERSATION.CHATBOT.FEEDBACK.SUBMITTED_BY', {
              agentName: submittedFeedback.submitted?.agent,
              time: formatDate(submittedFeedback.submitted?.at),
            })
          }}
        </p>
        <p
          v-if="submittedFeedback.chatbot?.comment"
          class="text-xs text-n-slate-11"
        >
          {{ submittedFeedback.chatbot?.comment }}
        </p>
      </div>
      <div v-else-if="displayMode === 'add_comment'" class="flex flex-col">
        <TextArea
          v-model="feedbackComment"
          class="mt-4"
          :placeholder="$t('CONVERSATION.CHATBOT.FEEDBACK.COMMENT_PLACEHOLDER')"
        />
        <NextButton
          sm
          class="w-40 self-center"
          :disabled="!feedbackComment"
          :label="$t('CONVERSATION.CHATBOT.FEEDBACK.SUBMIT_BUTTON')"
          @click="onSubmitComment"
        />
      </div>
    </div>
  </BaseBubble>
</template>
